import { useCallback, useEffect, useMemo, useState } from "react";

import {
  useImageLabeling as useZoltarImageLabeling,
  useZoltarMutation,
} from "@app/hooks/zoltar";
import { useUpdateAssets } from "@app/hooks/videoWizard";
import { readZoltarGenerateJsonWithFallback } from "@app/services/zoltar";

import { BRAND_LIBRARY_FIELDS } from "@app/utils/systemPrompts";

import { SKIP_IMAGE_LABELING_TEMPLATES } from "./variationPrompts";
import {
  loadingImageLabeling,
  labelingId,
  hasLabelingFields,
  imageLabelingResults,
} from "./shared";

const BRAND_VISIBILITY_PROFILE = "brand-visibility-library";
const DNA_FIELDS = ["Brand-visual-DNA", "Competitor-brand-visual-DNA"];
const IMAGE_LABELING_RETRIES = 2;

/** The asset with any labeling received for its current file merged back in. */
const withSavedLabeling = (projectId, asset) => {
  const saved = imageLabelingResults.get(labelingId(projectId, asset?.name));

  return saved && saved.filePath === asset?.filePath
    ? { ...asset, labeling: { ...asset.labeling, ...saved.labeling } }
    : asset;
};

/**
 * Describes reference images so the prompts built from them can talk about
 * what they show.
 *
 * Every asset of `assets` that has a file, a presigned URL and no `labeling`
 * yet is described once and the result is persisted onto the asset. The same
 * image can be referenced by more than one variation group — and by more than
 * one mounted hook — so the requests in flight are tracked project-wide (see
 * `loadingImageLabeling`), keeping it at one request per image.
 *
 * Usable on its own: pass the assets to describe plus the presigned URLs for
 * their file paths.
 *
 * @param {object} options
 * @param {string} options.projectId - The video/project the assets belong to.
 * @param {Array<object>} options.assets - Resolved reference assets, memoized.
 * @param {Map<string, string>} options.urlByPath - Presigned URL per file path.
 * @param {string} [options.templateId] - Template, to honour the skip list.
 * @param {string} [options.systemPromptId] - Labeling prompt ID to use.
 * @param {boolean} [options.enabled] - Whether labeling may run at all.
 * @param {string} [options.companyName] - Campaign company, for the brand library prompt.
 */
export const useImageLabeling = ({
  projectId,
  assets,
  urlByPath,
  templateId,
  imageVariations,
  systemPromptId = "common-image-description",
  enabled = true,
  companyName,
  product,
}) => {
  const updateAssets = useUpdateAssets(projectId);

  // Failed assets are skipped until `retry()`, so one failure does not block the rest.
  const [failedIds, setFailedIds] = useState(() => new Set());

  /**
   * Templates whose variation prompts never read `visual.labeling` skip
   * describing their reference images entirely.
   */
  const skipImageLabeling = SKIP_IMAGE_LABELING_TEMPLATES.has(templateId);

  const getDescriptionProfile = useCallback(
    (asset) =>
      imageVariations?.find((variation) => variation.key === asset?.name)
        ?.imageDescriptionPrompt === BRAND_VISIBILITY_PROFILE
        ? "Brand-Visibility-reference-description"
        : null,
    [imageVariations],
  );

  const hasLabeling = useCallback(
    (asset) => {
      const profile = getDescriptionProfile(asset);

      return hasLabelingFields(
        asset?.labeling,
        profile ? BRAND_LIBRARY_FIELDS : ["description"],
      );
    },
    [getDescriptionProfile],
  );

  const mutationOptions = {
    onMutate: (variables) => {
      loadingImageLabeling.add(labelingId(projectId, variables.key));
    },
    onSuccess: (data, variables) => {
      // React Query keeps the latest options, so this reads the assets as of
      // the moment the labeling settled.
      const asset = withSavedLabeling(
        projectId,
        assets?.find((a) => a?.name === variables.key) ?? {
          name: variables.key,
        },
      );
      const labeling = { ...asset.labeling, ...data };

      if (asset.filePath) {
        imageLabelingResults.set(labelingId(projectId, variables.key), {
          filePath: asset.filePath,
          labeling,
        });
      }

      return updateAssets.mutateAsync(
        {
          assets: [{ ...asset, labeling }],
        },
        {
          onSettled: () => {
            loadingImageLabeling.delete(labelingId(projectId, variables.key));
          },
        },
      );
    },
    onError: (error, variables) => {
      setFailedIds((prev) =>
        new Set(prev).add(labelingId(projectId, variables.key)),
      );
    },
    onSettled: (data, error, variables) => {
      loadingImageLabeling.delete(labelingId(projectId, variables.key));
    },
  };

  const imageLabeling = useZoltarImageLabeling(
    {
      ...mutationOptions,
      // Empty or unparsable model replies are usually transient.
      retry: IMAGE_LABELING_RETRIES,
      retryDelay: (attempt) => 1000 * 2 ** attempt,
    },
    (data, variables) => {
      if (variables.systemPromptId !== "Brand-Visibility-image-description") {
        return data;
      }

      if (!hasLabelingFields(data, ["description"])) {
        throw new Error(
          "Image labeling returned an invalid or empty reference description.",
        );
      }

      return { ...data, descriptionProfile: BRAND_VISIBILITY_PROFILE };
    },
  );
  const imageDna = useZoltarImageLabeling(
    {
      ...mutationOptions,
      retry: IMAGE_LABELING_RETRIES,
      retryDelay: (attempt) => 1000 * 2 ** attempt,
    },
    (data) => {
      if (!hasLabelingFields(data, DNA_FIELDS)) {
        throw new Error(
          "Image labeling returned an invalid or empty visual DNA.",
        );
      }

      return Object.fromEntries(
        DNA_FIELDS.map((field) => [field, data[field]]),
      );
    },
  );
  const imageLibrary = useZoltarMutation(
    {
      ...mutationOptions,
      meta: {
        errorMessage:
          "Failed to generate the brand image library. Please try again.",
      },
    },
    (response) => {
      const data = readZoltarGenerateJsonWithFallback(
        response,
        "{}",
        "Failed to generate the brand image library",
      );
      const fields = BRAND_LIBRARY_FIELDS;

      if (
        !hasLabelingFields(data, fields) ||
        Object.keys(data).length !== fields.length
      ) {
        throw new Error(
          "Image analysis returned an incomplete or invalid library.",
        );
      }

      return data;
    },
  );

  const { mutate: mutateImageLabeling, reset: resetImageLabeling } =
    imageLabeling;
  const { mutate: mutateImageDna, reset: resetImageDna } = imageDna;
  const { mutate: mutateImageLibrary, reset: resetImageLibrary } = imageLibrary;
  const { mutateAsync: writeAssets } = updateAssets;

  /** Clears previous failures so the failed assets are described again. */
  const retry = useCallback(() => {
    resetImageLabeling();
    resetImageDna();
    resetImageLibrary();
    setFailedIds((prev) => (prev.size ? new Set() : prev));
  }, [resetImageLabeling, resetImageDna, resetImageLibrary]);

  // Describe every reference image that still needs it.
  useEffect(() => {
    if (!enabled || skipImageLabeling) {
      return;
    }

    assets?.forEach((current) => {
      const presignURL = urlByPath?.get(current?.filePath);
      const profile = getDescriptionProfile(current);
      const id = labelingId(projectId, current?.name);
      const isDone = (a) => (profile ? hasLabeling(a) : !!a?.labeling);
      const asset = withSavedLabeling(projectId, current);

      if (
        !asset?.name ||
        !asset?.filePath ||
        !presignURL ||
        isDone(current) ||
        failedIds.has(id) ||
        loadingImageLabeling.has(id)
      ) {
        return;
      }

      if (isDone(asset)) {
        loadingImageLabeling.add(id);
        writeAssets({ assets: [asset] })
          .catch(() => {})
          .finally(() => loadingImageLabeling.delete(id));
        return;
      }

      // Brand Visibility runs in three persisted steps, so each one resumes
      // after a failure: description and DNA (imageLabeling), then the library.
      // A description from another prompt (e.g. copied from the reference on a
      // failed generation) is replaced by step 1 rather than reused.
      if (
        profile &&
        asset?.labeling?.descriptionProfile === BRAND_VISIBILITY_PROFILE &&
        hasLabelingFields(asset.labeling, ["description"])
      ) {
        const brandInput = [
          `COMPANY_NAME: ${companyName ?? ""}`,
          `PRODUCT: ${product ?? ""}`,
        ];
        const description = {
          description: asset.labeling.description,
          mainSubject: asset.labeling.mainSubject,
          keyElements: asset.labeling.keyElements,
          attributes: asset.labeling.attributes,
          productType: asset.labeling.productType,
          productMatch: asset.labeling.productMatch,
          visibleProduct: asset.labeling.visibleProduct,
        };

        if (!hasLabelingFields(asset.labeling, DNA_FIELDS)) {
          mutateImageDna({
            key: asset.name,
            imageUrl: presignURL,
            systemPromptId: "Brand-Visibility-image-dna",
            userInput: [
              ...brandInput,
              `REFERENCE_DESCRIPTION: ${JSON.stringify(description)}`,
            ].join("\n"),
          });
          return;
        }

        mutateImageLibrary({
          key: asset.name,
          promptTemplate: [],
          systemPromptId: profile,
          userInput: [
            ...brandInput,
            `REFERENCE_ANALYSIS: ${JSON.stringify({
              ...description,
              "Brand-visual-DNA": asset.labeling["Brand-visual-DNA"],
              "Competitor-brand-visual-DNA":
                asset.labeling["Competitor-brand-visual-DNA"],
            })}`,
          ].join("\n"),
        });
        return;
      }

      mutateImageLabeling({
        key: asset.name,
        imageUrl: presignURL,
        systemPromptId: profile
          ? "Brand-Visibility-image-description"
          : systemPromptId,
        ...(profile
          ? {
              userInput: [
                `COMPANY_NAME: ${companyName ?? ""}`,
                `PRODUCT: ${product ?? ""}`,
              ].join("\n"),
            }
          : {}),
      });
    });
  }, [
    enabled,
    skipImageLabeling,
    assets,
    urlByPath,
    projectId,
    systemPromptId,
    companyName,
    product,
    getDescriptionProfile,
    hasLabeling,
    failedIds,
    writeAssets,
    imageLabeling.isPending,
    imageLabeling.isSuccess,
    mutateImageLabeling,
    imageDna.isPending,
    imageDna.isSuccess,
    mutateImageDna,
    imageLibrary.isPending,
    imageLibrary.isSuccess,
    mutateImageLibrary,
  ]);

  /**
   * Whether an asset has everything a prompt needs from it — nothing to
   * describe, or a description already persisted.
   */
  const isLabeled = useCallback(
    (asset) => skipImageLabeling || !asset?.filePath || hasLabeling(asset),
    [skipImageLabeling, hasLabeling],
  );

  const isPending =
    imageLabeling.isPending || imageDna.isPending || imageLibrary.isPending;
  const isError =
    imageLabeling.isError || imageDna.isError || imageLibrary.isError;
  const error = imageLibrary.error ?? imageDna.error ?? imageLabeling.error;

  return useMemo(
    () => ({
      skipImageLabeling,
      isLabeled,
      isPending,
      isError,
      error,
      retry,
    }),
    [skipImageLabeling, isLabeled, isPending, isError, error, retry],
  );
};
